import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  authProtectedFlattenRoutes,
  publicProtectedFlattenRoutes,
  styledPublicRoutes,
} from "./index";
import { useDispatch, useSelector } from "react-redux";

import DefaultLayout from "../layouts/Default";
import DetachedLayout from "../layouts/Detached";
import { GET_USER } from "../graphql/dodavanjekorisnika";
import HorizontalLayout from "../layouts/Horizontal/";
import { ILocalStorage } from "../redux/interfaces";
import { LayoutTypes } from "../constants/layout";
import { RootState } from "../redux/store";
import ScrollToTop from "../components/ScrollToTop/ScrollToTop";
import TwoColumnLayout from "../layouts/TwoColumn/";
import VerticalLayout from "../layouts/Vertical";
import { logoutUser } from "../redux/auth/actions";
import { paths } from "./paths";
import { useLazyQuery } from "@apollo/client";

interface IRoutesProps {}

const AllRoutes = (props: IRoutesProps) => {
  const { layout } = useSelector((state: RootState) => ({
    layout: state.Layout,
  }));

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastUserIdRef = useRef<number | undefined>(undefined);
  const lastSessionIdRef = useRef<string | undefined>(undefined);

  // Make person reactive so it updates when localStorage changes
  const [person, setPerson] = useState<ILocalStorage | null>(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      try {
        return JSON.parse(storedUser);
      } catch {
        return null;
      }
    }
    return null;
  });

  // Listen for storage changes (e.g., when user logs in)
  useEffect(() => {
    const handleStorageChange = () => {
      const storedUser = localStorage.getItem("user");

      if (storedUser) {
        try {
          const parsed = JSON.parse(storedUser);
          // Only update if userId or sessionId actually changed to prevent unnecessary re-renders
          setPerson((prevPerson) => {
            if (
              prevPerson?.user?.userId === parsed?.user?.userId &&
              prevPerson?.user?.sessionId === parsed?.user?.sessionId
            ) {
              // No change, return previous value to prevent re-render
              return prevPerson;
            }
            return parsed;
          });
        } catch (error) {
          setPerson(null);
        }
      } else {
        setPerson((prevPerson) => {
          if (prevPerson === null) {
            return prevPerson; // Already null, no change
          }
          return null;
        });
      }
    };

    // Check immediately
    handleStorageChange();

    // Listen for storage events (from other tabs/windows)
    window.addEventListener("storage", handleStorageChange);

    // Poll localStorage less frequently (every 30 seconds) to catch changes from same tab
    // This is needed because storage events don't fire in the same tab
    // Reduced frequency to avoid excessive logging and re-renders
    const pollInterval = setInterval(handleStorageChange, 30000);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      clearInterval(pollInterval);
    };
  }, [location.pathname]);

  // Check if the session Id is valid
  const getLayout = () => {
    let layoutCls = TwoColumnLayout;

    switch (layout.layoutType) {
      case LayoutTypes.LAYOUT_HORIZONTAL:
        layoutCls = HorizontalLayout;
        break;
      case LayoutTypes.LAYOUT_DETACHED:
        layoutCls = DetachedLayout;
        break;
      case LayoutTypes.LAYOUT_VERTICAL:
        layoutCls = VerticalLayout;
        break;
      default:
        layoutCls = TwoColumnLayout;
        break;
    }
    return layoutCls;
  };

  let Layout = getLayout();

  // Extract stable userId and sessionId values using useMemo to prevent unnecessary re-runs
  // This ensures they only change when the actual values change, not when person object reference changes
  const userId = useMemo(() => person?.user?.userId, [person?.user?.userId]);
  const sessionId = useMemo(
    () => person?.user?.sessionId,
    [person?.user?.sessionId]
  );

  // Lazy query to check user active status
  const [checkUserActive] = useLazyQuery<{
    User: {
      userId: number;
      active?: boolean | null;
      type?: string | null;
      fullName?: string | null;
      position?: string | null;
    };
  }>(GET_USER, {
    fetchPolicy: "network-only", // Always fetch fresh data
    onCompleted: (data) => {
      // Only log out if we get a successful response AND active is explicitly false
      // Don't log out if active is null, undefined, or true
      if (data?.User && data.User.active === false) {
        dispatch(logoutUser());
        navigate(paths.auth.logout);
      }
    },
    onError: (error) => {
      // Don't log out on query errors - could be network issues, permissions, etc.
      // Only log out if we get a successful response with active === false
    },
  });

  // Periodic check for user active status (every 5 minutes)
  useEffect(() => {
    // Skip check if we're on auth routes (login, logout, etc.)
    const isAuthRoute = location.pathname.startsWith("/auth/");
    if (isAuthRoute) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    if (!person?.user.userId || !person?.user.sessionId) {
      // No user logged in, clear any existing interval
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      lastUserIdRef.current = undefined;
      lastSessionIdRef.current = undefined;
      return;
    }

    // Only set up check if userId or sessionId actually changed
    // Use the stable extracted values from outside the effect
    if (
      lastUserIdRef.current === userId &&
      lastSessionIdRef.current === sessionId &&
      intervalRef.current !== null
    ) {
      // Already set up for this user, don't re-run
      return;
    }

    // Update refs
    lastUserIdRef.current = userId;
    lastSessionIdRef.current = sessionId;

    // Clear any existing intervals/timeouts
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    // Perform initial check after a short delay to avoid race conditions
    // This gives the app time to fully initialize after login
    timeoutRef.current = setTimeout(() => {
      try {
        checkUserActive({
          variables: { userId: Number(userId) },
        });
      } catch (error) {
        // Silently handle errors
      }
    }, 2000); // 2 second delay

    // Set up interval to check user status every 5 minutes (300000ms)
    intervalRef.current = setInterval(() => {
      checkUserActive({
        variables: { userId: Number(userId) },
      });
    }, 5 * 60 * 1000); // 5 minutes in milliseconds

    // Cleanup interval and timeout on unmount or when user changes
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
    // eslint-disable-next-line
  }, [
    userId,
    sessionId,
    checkUserActive,
    dispatch,
    navigate,
    location.pathname,
  ]);

  useEffect(() => {
    // Only redirect to first login if:
    // 1. User exists
    // 2. lastLogin is null (in state AND in localStorage, to avoid redirect right after first login)
    // 3. We're not already on the first login page (to prevent redirect loops)
    if (location.pathname === paths.auth.firstLogin) return;

    if (!person?.user) return;

    // After first login we dispatch and navigate; person state may still be stale.
    // Read from localStorage so we don't redirect back when user just completed first login.
    const stored = localStorage.getItem("user");
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (parsed?.user?.lastLogin != null) {
          // Fix: prevent infinite loop by checking if values actually changed
          if (
            person?.user?.lastLogin !== parsed.user.lastLogin ||
            person?.user?.userId !== parsed.user.userId
          ) {
            setPerson(parsed);
          }
          return;
        }
      } catch {
        // ignore
      }
    }

    if (person.user.lastLogin === null) {
      navigate(paths.auth.firstLogin);
    }
  }, [person, navigate, location.pathname]);

  return (
    <React.Fragment>
      <ScrollToTop>
        <Routes>
          <Route>
            {publicProtectedFlattenRoutes.map((route, idx) => (
              <Route
                path={route.path}
                element={
                  <DefaultLayout {...props} layout={layout}>
                    {route.element}
                  </DefaultLayout>
                }
                key={idx}
              />
            ))}
          </Route>

          <Route>
            {styledPublicRoutes.map((route, idx) => (
              <Route
                path={route.path}
                element={<Layout {...props}>{route.element}</Layout>}
                key={idx}
              />
            ))}
          </Route>

          <Route>
            {authProtectedFlattenRoutes.map((route, idx) => {
              const hasSessionId = !!person?.user.sessionId;

              return (
                <Route
                  path={route.path}
                  element={
                    !hasSessionId ? (
                      <Navigate
                        to={{
                          pathname: "/auth/login",
                          search: "next=" + route.path,
                        }}
                        replace={true}
                      />
                    ) : (
                      <Layout {...props}>{route.element}</Layout>
                    )
                  }
                  key={idx}
                />
              );
            })}
          </Route>

          <Route path="*" element={<Navigate to={paths.errors.error404} />} />
        </Routes>
      </ScrollToTop>
    </React.Fragment>
  );
};

export default AllRoutes;
